import CustomSync from '@/components/custom-sync';
import { Button } from '@/components/ui/button';
import {
  Order_By,
  useCheckGiftCardQuery,
  useInfiniteGiftCardTransactionListQuery,
} from '@/gql/generated';
import useVerifySync from '@/hooks/use-verify-sync';
import { convertStatus, formatCode, formatNumberToCurrency } from '@/lib/utils';
import { FullScreenLoadingContext } from '@/pages/_providers/full-loading-overlay-provider';
import { SoundContext } from '@/pages/(gapsdk)/_provider/sound-provider';
import NoData from '@/pages/(guest)/_components/no-data';
import { normalizeTransactionInfinite } from '@/types/gift-card-transaction/transaction.dto';
import { useContext, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TransactionTable from '../_components/transaction-table';
import InvalidGiftCard from '../../_components/invalid-gift-card';
import TopNavigation from '../../_components/top-navigation';
import { useAuthenticatedProvider } from '../../../_provider/authenticated-provider';

export default function GiftCardInformationPage() {
  const navigate = useNavigate();
  const { event: eventSound } = useContext(SoundContext);
  const { code } = useParams();

  const { user } = useAuthenticatedProvider();
  const { event: eventFullScreenLoading } = useContext(
    FullScreenLoadingContext,
  );
  const { verifySynced, isSynced } = useVerifySync();
  const whmcsId = user.whmcs_id;

  const giftCheckGiftCardQuery = useCheckGiftCardQuery({
    where: {
      _or: [
        {
          _or: [
            {
              whmcs_id: { _is_null: true },
              whmcs_ids: { _contains: [whmcsId] },
              code: { _eq: code },
            },
            { whmcs_id: { _eq: whmcsId }, code: { _eq: code } },
          ],
        },
        { pos_gift_card: { code: { _eq: code }, whmcs_id: { _eq: whmcsId } } },
      ],
    },
  });

  const giftCardId = giftCheckGiftCardQuery.data?.gift_card[0]?.id;

  const pageSize = 30;

  const transactionList = useInfiniteGiftCardTransactionListQuery(
    {
      offset: 0,
      limit: pageSize,
      orderBy: [{ date: Order_By.Desc }],
      where: {
        whmcs_id: { _eq: whmcsId },
        gift_card_id: { _eq: giftCardId },
      },
    },
    {
      initialPageParam: { offset: 0 },
      getNextPageParam: (lastPage, allPages) => {
        return lastPage.transaction.length < pageSize
          ? undefined
          : { offset: allPages.length * pageSize };
      },
      refetchOnWindowFocus: false,
      select: normalizeTransactionInfinite,
    },
  );

  const isLoading =
    giftCheckGiftCardQuery.isLoading || giftCheckGiftCardQuery.isPending;

  useEffect(() => {
    eventFullScreenLoading.setIsLoading(isLoading, true);
  }, [isLoading]);

  useEffect(() => {
    verifySynced(new Date(giftCheckGiftCardQuery?.dataUpdatedAt ?? 0));
  }, [giftCheckGiftCardQuery?.dataUpdatedAt]);

  useEffect(() => {
    if (!isSynced) {
      giftCheckGiftCardQuery?.refetch();
    }
    return () => {
      eventFullScreenLoading.setIsLoading(false, false);
    };
  }, []);

  return (
    <div className="flex size-full flex-col items-center gap-8 px-24">
      <TopNavigation onBack={() => navigate(`/gift-card/verify`)} />
      {giftCheckGiftCardQuery.isFetched && (
        <>
          {!giftCheckGiftCardQuery.data?.gift_card[0]?.id ? (
            <InvalidGiftCard />
          ) : (
            <div className="flex size-full flex-col items-center">
              <div className="flex flex-none flex-col items-center gap-2 py-12">
                <h2 className="text-2xl">Balance</h2>
                <span className="text-7xl font-bold">
                  {formatNumberToCurrency(
                    giftCheckGiftCardQuery.data?.gift_card[0]?.current_balance,
                  )}
                </span>
              </div>

              <div className="w-full flex-none space-y-8 border-y border-gray-200 py-6">
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-medium">Gift card</span>
                  <span className="text-2xl font-bold">
                    {formatCode(code ?? '')}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-2xl font-medium">Status</span>
                  <span className="text-2xl font-bold">
                    {convertStatus(
                      giftCheckGiftCardQuery.data.gift_card[0].status,
                    )}
                  </span>
                </div>
              </div>

              <div className="flex w-full flex-none items-center gap-4 py-8">
                <div
                  onClick={() => {
                    eventSound.playTap();
                    navigate(
                      `/gift-card/add-fund/${code}?id=${giftCheckGiftCardQuery.data?.gift_card[0]?.id}`,
                    );
                  }}
                  className="flex h-16 flex-1 items-center justify-center rounded-lg bg-emerald-500 py-4 text-2xl font-bold text-white transition-colors hover:bg-emerald-600"
                >
                  Add funds
                </div>
                <Button
                  disabled={
                    giftCheckGiftCardQuery.data?.gift_card[0]
                      ?.current_balance === 0
                  }
                  onClick={() => {
                    eventSound.playTap();
                    navigate(
                      `/gift-card/redeem/${code}?id=${giftCheckGiftCardQuery.data?.gift_card[0]?.id}&current_balance=${giftCheckGiftCardQuery.data?.gift_card[0]?.current_balance}`,
                    );
                  }}
                  className="flex h-16 flex-1 items-center justify-center rounded-lg bg-blue-500 py-4 text-2xl font-bold text-white transition-colors hover:bg-blue-600"
                >
                  Redeem
                </Button>
              </div>
              <div className="w-full grow overflow-hidden">
                {giftCheckGiftCardQuery.data?.gift_card[0].transactions
                  ?.length === 0 ? (
                  <NoData />
                ) : (
                  <TransactionTable
                    whmcsId={whmcsId}
                    giftCardId={giftCardId ?? ''}
                    timezone={user.time_zone!}
                    queryResult={transactionList}
                  />
                )}
              </div>
            </div>
          )}
        </>
      )}
      <CustomSync
        isSynced={isSynced}
        isSyncing={giftCheckGiftCardQuery?.isFetching ?? false}
        onSync={() => {
          giftCheckGiftCardQuery?.refetch();
          transactionList.refetch();
        }}
      />
    </div>
  );
}
